import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, List, Typography } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { getErrorMessage } from '@billda/shared-ui';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';
import { useSiteFiles } from '../../hooks/useSiteFiles';
import { useStorageUsage } from '../../hooks/useStorageUsage';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import { useSitePanelState } from '../../hooks/useSitePanelState';
import { useUploadTrayStore } from '../../stores/uploadTrayStore';
import { FileRow } from './FileRow';
import {
  BatchCollisionDialog,
  type CollisionItem,
} from './BatchCollisionDialog';
import { formatFileSize } from './formatFileSize';
import { StorageBar } from './StorageBar';

interface SiteFileListProps {
  siteId: number;
  uploadButtonRef?: React.RefObject<HTMLInputElement | null>;
}

export function SiteFileList({ siteId, uploadButtonRef }: SiteFileListProps) {
  const { data, isLoading, error } = useSiteFiles(siteId);
  const { openPanel } = useSitePanelState();
  const hasWritePermission = usePermission(Permissions.Files.Write);
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const internalRef = useRef<HTMLInputElement>(null);
  const fileInputRef = uploadButtonRef ?? internalRef;
  const { data: storageUsage } = useStorageUsage();
  const addItems = useUploadTrayStore(state => state.addItems);

  const [isDragOver, setIsDragOver] = useState(false);

  const [collisionState, setCollisionState] = useState<{
    open: boolean;
    collisions: CollisionItem[];
  }>({ open: false, collisions: [] });

  const allFiles = data
    ? [
        ...new Map(
          Object.values(data)
            .flatMap(files => files)
            .map(f => [f.id, f])
        ).values(),
      ]
    : [];

  const getAllFileNames = useCallback((): string[] => {
    if (!data) {
      return [];
    }
    return Object.values(data).flatMap(files => files.map(f => f.name));
  }, [data]);

  const generateUniqueName = useCallback(
    (name: string): string => {
      const existingNames = getAllFileNames();
      const dotIndex = name.lastIndexOf('.');
      const baseName = dotIndex > 0 ? name.slice(0, dotIndex) : name;
      const ext = dotIndex > 0 ? name.slice(dotIndex) : '';

      let counter = 1;
      let candidate = `${baseName} (${counter})${ext}`;
      while (existingNames.includes(candidate)) {
        counter++;
        candidate = `${baseName} (${counter})${ext}`;
      }
      return candidate;
    },
    [getAllFileNames]
  );

  const handleFilesSelected = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) {
        return;
      }

      // Pre-flight storage check
      if (storageUsage) {
        const totalSize = fileArray.reduce((sum, f) => sum + f.size, 0);
        const remaining =
          storageUsage.maxStorageSizeBytes -
          storageUsage.currentStorageUsageBytes;
        if (totalSize > remaining) {
          showSnackbar(
            `These files total ${formatFileSize(totalSize)} but you only have ${formatFileSize(remaining)} remaining`,
            'error'
          );
          return;
        }
      }

      // Per-file max size check
      const validFiles: File[] = [];
      for (const file of fileArray) {
        if (storageUsage && file.size > storageUsage.maxFileSizeBytes) {
          showSnackbar(
            `${file.name} exceeds the ${formatFileSize(storageUsage.maxFileSizeBytes)} file size limit`,
            'warning'
          );
        } else {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) {
        return;
      }

      // Check for name collisions
      const existingNames = getAllFileNames();
      const nonColliding: Array<{ file: File; fileName: string }> = [];
      const collisions: CollisionItem[] = [];

      for (const file of validFiles) {
        if (existingNames.includes(file.name)) {
          collisions.push({
            file,
            originalName: file.name,
            suggestedName: generateUniqueName(file.name),
          });
        } else {
          nonColliding.push({ file, fileName: file.name });
        }
      }

      // Add non-colliding files immediately
      if (nonColliding.length > 0) {
        addItems(
          nonColliding.map(({ file, fileName }) => ({
            file,
            fileName,
            siteId,
          }))
        );
      }

      // Show collision dialog if needed
      if (collisions.length > 0) {
        setCollisionState({ open: true, collisions });
      }
    },
    [
      storageUsage,
      getAllFileNames,
      generateUniqueName,
      addItems,
      siteId,
      showSnackbar,
    ]
  );

  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }
      // Copy before clearing — resetting the input invalidates the FileList
      const fileArray = Array.from(event.target.files);
      event.target.value = '';
      handleFilesSelected(fileArray);
    },
    [handleFilesSelected]
  );

  const handleCollisionResolve = useCallback(
    (resolved: Array<{ file: File; fileName: string }>) => {
      if (resolved.length > 0) {
        addItems(
          resolved.map(({ file, fileName }) => ({
            file,
            fileName,
            siteId,
          }))
        );
      }
      setCollisionState({ open: false, collisions: [] });
    },
    [addItems, siteId]
  );

  const handleCollisionCancel = useCallback(() => {
    setCollisionState({ open: false, collisions: [] });
  }, []);

  // Prevent browser default file-drop behavior (opening the file) on the whole window
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  // Drag-and-drop handlers
  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (hasWritePermission) {
        setIsDragOver(true);
      }
    },
    [hasWritePermission]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (hasWritePermission) {
        setIsDragOver(true);
      }
    },
    [hasWritePermission]
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;
    const currentTarget = event.currentTarget as Node;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      if (hasWritePermission && event.dataTransfer.files.length > 0) {
        const fileArray = Array.from(event.dataTransfer.files);
        handleFilesSelected(fileArray);
      }
    },
    [hasWritePermission, handleFilesSelected]
  );

  if (error) {
    return (
      <Typography
        variant="body2"
        color="error"
        sx={{ textAlign: 'center', py: 6 }}
        data-testid="site-file-list"
      >
        {getErrorMessage(error)}
      </Typography>
    );
  }

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept=".pdf,.txt,.rtf,.png,.jpg,.jpeg,.heic,.heif,.webp,.tiff,.tif,.doc,.docx,.odt,.csv,.xls,.xlsx,.ods,.pptx,.odp,.zip"
        style={{ display: 'none' }}
        data-testid="file-input"
        multiple
      />

      <BatchCollisionDialog
        open={collisionState.open}
        collisions={collisionState.collisions}
        onResolve={handleCollisionResolve}
        onCancel={handleCollisionCancel}
      />

      {hasWritePermission && <StorageBar />}

      <Box
        data-testid="file-drop-zone"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Drop overlay */}
        {isDragOver && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" color="primary">
              Drop files here to upload
            </Typography>
          </Box>
        )}

        <ListViewShell
          itemCount={allFiles.length}
          loading={isLoading}
          emptyMessage="No files"
          emptyIcon={
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 64, color: 'text.disabled' }}
            />
          }
          emptyTitle="No files yet"
          emptyHelperText={
            hasWritePermission
              ? 'Upload files to share documents with your team'
              : undefined
          }
          data-testid="site-file-list"
        >
          <List disablePadding>
            {allFiles.map(file => (
              <FileRow
                key={file.id}
                file={file}
                onClick={() => openPanel('file', file.id)}
              />
            ))}
          </List>
        </ListViewShell>

        {/* Empty state upload CTA */}
        {!isLoading && allFiles.length === 0 && hasWritePermission && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-file-button"
            >
              Upload a file
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}
